import { useState, useRef } from 'react'
import {
   ICONS_MAP,
   PRIMARY_FILTER_OPTIONS,
} from '@forwardflow/constants/onboarding.constants'
import { Group, Select, MultiSelect } from '@forwardflow/ui-v2'
import { SelectProps, Loader } from '@mantine/core'
import { IFilterCriteria } from '@forwardflow/types/onboarding'
import { CloseIcon } from '@forwardflow/icons'
import { useLeadOptions } from '@forwardflow/hooks/customHooks/useLeadOptions'
import { useDebouncedValue } from '@forwardflow/hooks/customHooks/useDebouncedValue'
import { TLeadType } from '@forwardflow/types/campaign.types'

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
   <Group flex="1" gap="xs">
      {ICONS_MAP[option.value as keyof typeof ICONS_MAP]}
      {option.label}
   </Group>
)

interface FilterRowProps {
   filterCriteria: IFilterCriteria
   onUpdate: (filter: IFilterCriteria) => void
   onRemove: () => void
   usedFields: Set<string>
}

export const FilterRow = ({
   filterCriteria,
   onUpdate,
   onRemove,
   usedFields,
}: FilterRowProps) => {
   const [searchValue, setSearchValue] = useState('')
   const [debouncedSearch] = useDebouncedValue(searchValue, 500)
   const { options, isLoading, loadMore, hasMore } = useLeadOptions(
      filterCriteria.field as TLeadType,
      debouncedSearch
   )
   const dropdownRef = useRef<HTMLElement | null>(null)
   const isLoadingMore = useRef(false)

   // Get available field options (exclude already used fields except the current one)
   const availableFieldOptions = PRIMARY_FILTER_OPTIONS.filter(
      (option) =>
         option.value === filterCriteria.field || !usedFields.has(option.value)
   )

   const handleFieldChange = (value: string | null) => {
      if (!value) return
      setSearchValue('') // Reset search when field changes
      onUpdate({
         ...filterCriteria,
         field: value as 'source' | 'stage' | 'tags' | 'assigned_pond_id',
         value: [], // Reset values when field changes
      })
   }

   const handleValuesChange = (values: string[]) => {
      onUpdate({
         ...filterCriteria,
         value: values,
      })
   }

   const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (hasMore && !isLoading && !isLoadingMore.current) {
         const { scrollHeight, clientHeight, scrollTop } = target
         // Load more when user scrolls within 100px of bottom
         if (scrollTop + clientHeight >= scrollHeight - 100) {
            isLoadingMore.current = true
            loadMore()
            // Reset flag after a delay to prevent rapid calls
            setTimeout(() => {
               isLoadingMore.current = false
            }, 1000)
         }
      }
   }

   return (
      <div className="grid grid-cols-[1fr_3fr_auto] gap-4 items-center">
         <Select
            key={`selectedField-${filterCriteria.field}`}
            leftSection={
               ICONS_MAP[filterCriteria.field as keyof typeof ICONS_MAP]
            }
            data={availableFieldOptions}
            renderOption={renderSelectOption}
            value={filterCriteria.field}
            onChange={handleFieldChange}
            placeholder="Select a field"
         />
         <MultiSelect
            searchable
            key={`selectedValues-${filterCriteria.field}`}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            data={options}
            value={filterCriteria.value.map(String)}
            onChange={handleValuesChange}
            placeholder="Select values"
            rightSection={isLoading ? <Loader size="xs" /> : undefined}
            onDropdownOpen={() => {
               // Attach scroll listener when dropdown opens
               setTimeout(() => {
                  const dropdown = document.querySelector(
                     '.mantine-MultiSelect-dropdown .mantine-ScrollArea-viewport'
                  ) as HTMLElement
                  if (dropdown) {
                     dropdown.addEventListener('scroll', handleScroll)
                     dropdownRef.current = dropdown
                  }
               }, 0)
            }}
            onDropdownClose={() => {
               // Clean up scroll listener
               if (dropdownRef.current) {
                  dropdownRef.current.removeEventListener(
                     'scroll',
                     handleScroll
                  )
                  dropdownRef.current = null
               }
            }}
         />
         <CloseIcon width={16} height={16} onClick={onRemove} />
      </div>
   )
}
